/**
 * Single gate for AUTOMATIC page reloads triggered by the update pipeline
 * (new SW taking control). On a kitchen tablet an auto-reload while the
 * operator is filling in a HACCP record is real data loss — so a reload is
 * deferred while any registered blocker reports activity (open form dialog,
 * in-flight mutation) and fires as soon as the last blocker clears.
 *
 * User-initiated reloads (tap on the update banner) do NOT go through the
 * gate: an explicit tap is consent.
 */

type Blocker = () => boolean;

const blockers = new Set<Blocker>();
let pendingReload: ReturnType<typeof setInterval> | null = null;

export const RELOAD_GATE_POLL_MS = 2_000;

export function registerReloadBlocker(blocker: Blocker): () => void {
  blockers.add(blocker);
  return () => {
    blockers.delete(blocker);
  };
}

export function isReloadBlocked(): boolean {
  for (const blocker of blockers) {
    try {
      if (blocker()) {
        return true;
      }
    } catch {
      // A broken blocker must never pin the app to an old version.
    }
  }
  return false;
}

export function requestGatedReload(
  reload: () => void = () => window.location.reload(),
  pollMs: number = RELOAD_GATE_POLL_MS,
): void {
  if (pendingReload !== null) {
    return;
  }
  if (!isReloadBlocked()) {
    reload();
    return;
  }
  pendingReload = setInterval(() => {
    if (!isReloadBlocked()) {
      if (pendingReload !== null) {
        clearInterval(pendingReload);
        pendingReload = null;
      }
      reload();
    }
  }, pollMs);
}

export function resetReloadGateForTests(): void {
  blockers.clear();
  if (pendingReload !== null) {
    clearInterval(pendingReload);
    pendingReload = null;
  }
}
